import { Matrix4, PerspectiveCamera, Quaternion, Vector3 } from "three";

import { passageCrossing, type RoutePassage } from "./route";

/**
 * A camera that walks the exhibition on the visitor's behalf.
 *
 * Free movement answers "where may I go?" and nothing else, so a visitor who does not
 * already know the collection has no reason to go anywhere in particular. The tour
 * answers "where should I look, and in what order?" — it is the curator's reading of the
 * room, delivered as motion.
 *
 * It is a guide, not a rail. Any movement input hands control straight back, and the
 * tour can be picked up again from wherever the visitor left off.
 */

/** Metres per second along the floor. A slow walk — this is a room, not a corridor. */
const SPEED = 1.15;
/** Seconds held on each work before moving on. */
const DWELL = 5.5;
/** How fast the gaze catches up with where it should be pointing, per second. */
const TURN = 2.4;
/** Distance from a stop at which the gaze commits to the work rather than the path. */
const FRAMING = 3.2;
/** Length of the ease at each end of a leg, so the camera never starts or stops abruptly. */
const EASE = 1.4;
/**
 * The longest step the tour will take in one frame, in seconds.
 *
 * Generous on purpose. The tour follows a path that is already known to be clear, so a
 * long step cannot put the camera through a wall — and a tight clamp would slow the tour
 * down on precisely the low-powered hardware a kiosk uses. It exists only to stop a
 * backgrounded tab from lurching a whole minute forward when it returns.
 */
const MAX_STEP = 0.25;
const UP = new Vector3(0, 1, 0);

export type TourStop = {
  artworkId: string;
  title: string;
  roomId: string;
  /** Where the camera stands to look at the work. */
  view: Vector3;
  /** The centre of the work itself. */
  target: Vector3;
};

export type TourPhase = "travelling" | "dwelling" | "stopped";

/** Raised as the camera settles on a work, and once more when the last one is done. */
export type TourEvent =
  | { kind: "arrive"; index: number; stop: TourStop }
  | { kind: "finish" };

export class CinematicTour {
  readonly #camera: PerspectiveCamera;
  readonly #stops: readonly TourStop[];
  readonly #passage: RoutePassage | null;
  readonly #eyeHeight: number;
  readonly #gaze = new Matrix4();
  #phase: TourPhase = "stopped";
  #index = 0;
  /** Cumulative arc lengths of the current leg, so a distance maps to a position. */
  #path: Vector3[] = [];
  #lengths: number[] = [];
  #travelled = 0;
  #dwelt = 0;
  #bob = 0;

  constructor(
    camera: PerspectiveCamera,
    stops: readonly TourStop[],
    passage: RoutePassage | null,
    eyeHeight: number,
  ) {
    if (stops.length === 0) throw new Error("TOUR_STOPS_EMPTY");
    this.#camera = camera;
    this.#stops = stops;
    this.#passage = passage;
    this.#eyeHeight = eyeHeight;
  }

  get phase(): TourPhase {
    return this.#phase;
  }

  get running(): boolean {
    return this.#phase !== "stopped";
  }

  get index(): number {
    return this.#index;
  }

  get length(): number {
    return this.#stops.length;
  }

  get current(): TourStop {
    const stop = this.#stops[this.#index];
    if (stop === undefined) throw new Error("TOUR_STATE_INVALID");
    return stop;
  }

  /**
   * Begin at `index`, walking there from wherever the camera currently stands.
   *
   * `roomAt` reports which room a point is in; the tour needs it to know whether its
   * opening walk has to cross the corridor. Everything after that it knows already,
   * because the stops carry their own room.
   */
  start(index: number, roomAt: (point: Vector3) => string): void {
    this.#index = Math.min(Math.max(index, 0), this.#stops.length - 1);
    this.#phase = "travelling";
    this.#dwelt = 0;
    const from = this.#camera.position.clone();
    this.#layPath(from, roomAt(from));
  }

  halt(): void {
    this.#phase = "stopped";
  }

  update(deltaSeconds: number): TourEvent | null {
    if (this.#phase === "stopped") return null;
    const delta = Math.min(Math.max(deltaSeconds, 0), MAX_STEP);
    return this.#phase === "travelling" ? this.#travel(delta) : this.#dwell(delta);
  }

  #travel(delta: number): TourEvent | null {
    const total = this.#total();
    // A leg with no length at all — already standing on the stop — still has to hand
    // over to the dwell, or the tour would sit here forever waiting to arrive.
    if (total <= 0.001) return this.#arrive();
    this.#travelled = Math.min(this.#travelled + SPEED * this.#pace(total) * delta, total);
    this.#bob += SPEED * delta;

    const position = this.#pointAt(this.#travelled);
    // A gait, not a bounce: an amplitude you notice only when it stops.
    position.y = this.#eyeHeight + Math.sin(this.#bob * 2.6) * 0.014;
    this.#camera.position.copy(position);
    this.#aim(this.#lookWhileMoving(total), delta);

    return this.#travelled >= total ? this.#arrive() : null;
  }

  #dwell(delta: number): TourEvent | null {
    this.#dwelt += delta;
    // The held frame drifts a little across the work rather than freezing on it, which
    // is the difference between a camera watching something and a screenshot.
    const drift = Math.sin(this.#dwelt * 0.42) * 0.22;
    const stop = this.current;
    const sideways = new Vector3()
      .subVectors(stop.target, stop.view)
      .cross(UP);
    if (sideways.lengthSq() > 0) sideways.normalize();
    this.#aim(stop.target.clone().addScaledVector(sideways, drift), delta);
    if (this.#dwelt < DWELL) return null;

    if (this.#index >= this.#stops.length - 1) {
      this.#phase = "stopped";
      return { kind: "finish" };
    }
    const previous = stop;
    this.#index += 1;
    this.#phase = "travelling";
    this.#layPath(previous.view, previous.roomId);
    return null;
  }

  #arrive(): TourEvent | null {
    if (this.#phase !== "travelling") return null;
    this.#phase = "dwelling";
    this.#dwelt = 0;
    this.#camera.position.set(this.current.view.x, this.#eyeHeight, this.current.view.z);
    return { kind: "arrive", index: this.#index, stop: this.current };
  }

  /** Speed multiplier: ease out of the last stop and into the next one. */
  #pace(total: number): number {
    const ease = Math.min(EASE, total / 2);
    if (ease <= 0) return 1;
    const rising = Math.min(this.#travelled / ease, 1);
    const falling = Math.min((total - this.#travelled) / ease, 1);
    // Smoothstep on both ends, floored so the camera never stalls short of the stop.
    const shape = Math.min(rising, falling);
    return 0.12 + 0.88 * shape * shape * (3 - 2 * shape);
  }

  #lookWhileMoving(total: number): Vector3 {
    const stop = this.current;
    const remaining = total - this.#travelled;
    const ahead = this.#pointAt(Math.min(this.#travelled + 3, total));
    ahead.y = this.#eyeHeight;
    // Outside the framing distance the camera watches where it is going; inside it, the
    // gaze crosses over to the work, so the visitor arrives already looking at it.
    const weight = clamp((FRAMING - remaining) / FRAMING, 0, 1);
    return ahead.lerp(stop.target, weight * weight * (3 - 2 * weight));
  }

  #aim(lookAt: Vector3, delta: number): void {
    // Matrix4.lookAt, not Object3D.lookAt: the latter aims an object's +z at the target,
    // and a camera looks down its -z. Using it here points the tour backwards down the
    // room, away from every work it stops at.
    this.#gaze.lookAt(this.#camera.position, lookAt, UP);
    const desired = new Quaternion().setFromRotationMatrix(this.#gaze);
    // Frame-rate independent catch-up: the same curve at 30fps and at 144.
    this.#camera.quaternion.slerp(desired, 1 - Math.exp(-TURN * delta));
  }

  #layPath(from: Vector3, fromRoomId: string): void {
    const stop = this.current;
    const flat = new Vector3(from.x, this.#eyeHeight, from.z);
    const waypoints = [flat];
    if (this.#passage !== null) {
      for (const point of passageCrossing(
        this.#passage,
        fromRoomId,
        stop.roomId,
        this.#eyeHeight,
      )) {
        waypoints.push(point);
      }
    }
    waypoints.push(new Vector3(stop.view.x, this.#eyeHeight, stop.view.z));

    this.#path = waypoints;
    this.#lengths = [0];
    for (let i = 1; i < waypoints.length; i += 1) {
      const previous = waypoints[i - 1];
      const current = waypoints[i];
      if (previous === undefined || current === undefined) throw new Error("TOUR_PATH_INVALID");
      this.#lengths.push((this.#lengths[i - 1] ?? 0) + previous.distanceTo(current));
    }
    this.#travelled = 0;
  }

  #total(): number {
    return this.#lengths[this.#lengths.length - 1] ?? 0;
  }

  #pointAt(distance: number): Vector3 {
    const first = this.#path[0];
    if (first === undefined) throw new Error("TOUR_PATH_INVALID");
    for (let i = 1; i < this.#path.length; i += 1) {
      const end = this.#lengths[i] ?? 0;
      if (distance > end) continue;
      const start = this.#lengths[i - 1] ?? 0;
      const from = this.#path[i - 1];
      const to = this.#path[i];
      if (from === undefined || to === undefined) throw new Error("TOUR_PATH_INVALID");
      const span = end - start;
      return from.clone().lerp(to, span <= 0 ? 1 : (distance - start) / span);
    }
    return (this.#path[this.#path.length - 1] ?? first).clone();
  }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}
